import { useTranslation } from 'react-i18next';
import { Element } from 'react-scroll';
import { type ComponentWall as ComponentWallType } from '@cx-sdk/types';
import { type BookmakersConfig as ApiBookmakersConfig } from '@cx-web/api-clients/site-config-api/types';
import { type SubsProduct } from '@cx-web/domain-types';
import { useMyOCUser } from '@cx-web/feature-myoc';
import { useRepubContext } from '@cx-web/feature-repub';

import { type BookmakersConfig } from '@cx-modules/types';

import { ComponentWall } from '~/components/component-wall/ComponentWall.tsx';
import { type BetInsightsConfig } from '~/configuration/BetInsightsConfiguration';
import { type BettingInsightsContent, type EsIndex } from '~/configuration/types';

import styles from './styles/BettingInsights.module.scss';
import { BettingInsightsErrorState } from './BettingInsightsErrorState';
import { BettingInsightsLoadingState } from './BettingInsightsLoadingState';
import { BettingInsightsPagination } from './BettingInsightsPagination';
import { BettingInsightsRefinements } from './BettingInsightsRefinements';
import { BettingInsightsResults } from './BettingInsightsResults';

export default function BettingInsightsComponent<
  TIndex extends EsIndex,
  TContent extends BettingInsightsContent<TIndex> = BettingInsightsContent<TIndex>,
>({
  availableProducts,
  bookmakers,
  config,
  currentPath,
  indexName,
  redirectPath,
  resourcesUrl,
  subscriptionStatus,
  userHasCancelledSubscription,
}: {
  availableProducts: SubsProduct[];
  bookmakers: ApiBookmakersConfig;
  config: BetInsightsConfig<TIndex, TContent>;
  currentPath: string;
  indexName: TIndex;
  redirectPath: string | undefined;
  resourcesUrl: string;
  subscriptionStatus: {
    hasEssentialSubscription: boolean;
    hasPremiumSubscription: boolean;
  };
  userHasCancelledSubscription: boolean;
  wallType?: ComponentWallType['type'];
}) {
  const searchConfig = config?.ui?.search;
  const searchAttributes = config.searchAttributes;

  const searchComponent = searchConfig ? searchConfig.render({ searchAttributes }) : null;

  const { myocData } = useMyOCUser();
  const { repubIs, repubPath } = useRepubContext();
  const { t } = useTranslation();

  return (
    <>
      <BettingInsightsRefinements
        bookmakers={bookmakers as BookmakersConfig}
        config={config}
        currentPath={currentPath}
        indexName={indexName}
        isLoggedIn={!!myocData?.myOCUser}
        repubIs={repubIs}
        repubPath={repubPath}
        resourcesUrl={resourcesUrl}
      />
      <section className={styles.bettingInsightsSection} data-testid="results-section">
        {searchComponent}
        <BettingInsightsResults
          bookmakers={bookmakers as BookmakersConfig}
          config={config}
          resourcesUrl={resourcesUrl}
        />
        <BettingInsightsLoadingState />
        <BettingInsightsErrorState indexName={indexName} />
        {config.isWalled && (
          <Element name="componentWall" style={{ margin: '17px 0' }}>
            <ComponentWall
              availableProducts={availableProducts}
              currentPath={currentPath}
              redirectPath={redirectPath}
              subscriptionStatus={subscriptionStatus}
              userHasCancelledSubscription={userHasCancelledSubscription}
              walledFeature={config.tags.join(', ')}
            />
          </Element>
        )}
        <BettingInsightsPagination config={config} />
        {repubIs.GZ && <div className={styles.disclaimer}>{t('CX_BETTING_INSIGHTS_DISCLAIMER')}</div>}
      </section>
    </>
  );
}
